import { useSetContainerValues } from '@/components/container/context/context';
import { groupServiceAccounts } from '../-model/group-service-accounts';
import { type ServiceProps } from '../-model/props';
import {
  ActionCard,
  Alert,
  Button,
  cn,
  If,
  ImageProfile,
  mage,
  useI18n,
  useImgSrcWithFallback,
} from '@nosinovacao/nosid-mfe-common';
import { SkeletonLight } from '@/components/skeleton/skeleton.light';
import { type AccountServices, type Application } from '@/portal-api.definition';
import { ProfilePictureLight } from '@/components/profile-picture/profile-picture.light';
import { Info, Loader, Warning } from '@nosinovacao/fe-icons';
import { Link, Outlet } from '@tanstack/react-router';

export function ServicesLight({
  servicesLoading,
  services,
  defaultAccountId,
  applications,
  applicationsLoading,
  appIdRequestingAccess,
  requestAccess,
  removeServices,
  removeServicesLoading,
}: Readonly<ServiceProps>) {
  const { t } = useI18n();
  useSetContainerValues({
    title: t('Services.Title'),
  });
  return (
    <>
      <main className="grid gap-6 desktop-md:grid-cols-[1fr_minmax(16rem,max-content)] desktop-lg:grid-cols-[1fr_minmax(21.25rem,max-content)]">
        <SkeletonLight loading={servicesLoading} className="h-32 rounded-lg">
          <If condition={services.Type === 'Primary'}>
            <PrimaryUserMainSection services={services} defaultAccountId={defaultAccountId} />
          </If>
          <If condition={services.Type === 'Secondary'}>
            <SecondaryUserMainSection
              services={services}
              applications={applications}
              applicationsLoading={applicationsLoading}
              requestAccess={requestAccess}
              appIdRequestingAccess={appIdRequestingAccess}
            />
          </If>
        </SkeletonLight>
        <div className="grid gap-2">
          <SkeletonLight loading={servicesLoading} className="h-[74px] rounded" count={3}>
            <If condition={services.Type === 'Secondary'}>
              <RemoveServicesCard
                isLoading={removeServicesLoading}
                removeServices={removeServices}
                className="desktop-md:sticky desktop-md:top-28"
              />
            </If>
            <If condition={services.Type === 'Primary'}>
              <PrimaryUserAsideSection
                isLoading={removeServicesLoading}
                removeServices={removeServices}
                hasMultipleServices={services.Holder.ServiceAccounts.length > 1}
              />
            </If>
          </SkeletonLight>
        </div>
      </main>
      <Outlet />
    </>
  );
}

function SecondaryUserMainSection({
  services,
  applications,
  applicationsLoading,
  requestAccess,
  appIdRequestingAccess,
}: {
  services: AccountServices;
  applications: Application[];
  applicationsLoading: boolean;
  appIdRequestingAccess?: string | null;
  requestAccess: (id: string) => void;
}) {
  const { t } = useI18n();
  const accountHolderName = services.Holder.PrimaryUserDisplayName;
  const image = mage.getImage(services.Holder.PictureUri, ImageProfile.App);
  return (
    <div className="grid gap-4">
      <div className="grid gap-5 rounded-lg border border-nos-neutral-100 bg-nos-neutral-0 p-4 shadow-1">
        <span
          className="font-medium text-nos-neutral-900 font-title-h4"
          data-tid="title-secondary-user"
        >
          {t('Services.SecondaryUser.CardTitle')}
        </span>
        <div className="flex shrink-0 items-center gap-4">
          <ProfilePictureLight name={accountHolderName} src={image} size={48} />
          <span className="text-nos-neutral-900 font-text-p1">{accountHolderName}</span>
        </div>
      </div>
      <span className="font-light text-nos-neutral-900 font-text-p1" data-tid="title-apps">
        {t('Apps.Title')}
      </span>
      <div className="grid gap-2">
        <SkeletonLight
          loading={applicationsLoading}
          count={5}
          className="h-16 w-full rounded shadow-1"
        >
          <div className="grid gap-2">
            {applications.map((app) => {
              const isLoading = appIdRequestingAccess === app.Id;
              const appNotIncluded =
                !app.IsAuthorized && !app.CanRequestAccess && !app.AdminUsersOnly;
              const adminOnly = !app.IsAuthorized && !app.CanRequestAccess && app.AdminUsersOnly;
              const canRequestAccess =
                !app.IsAuthorized && app.CanRequestAccess && !app.AdminUsersOnly;
              return (
                <SecondaryUserApplicationCard
                  key={app.Id}
                  name={app.Name}
                  logo={app.LogoUrl}
                  adminOnly={adminOnly}
                  appNotIncluded={appNotIncluded}
                  isLoading={isLoading}
                  canRequestAccess={canRequestAccess}
                  isAuthorized={app.IsAuthorized}
                  id={app.Id}
                  requestAccess={requestAccess}
                  inactive={!app.IsAuthorized && !app.CanRequestAccess}
                />
              );
            })}
          </div>
        </SkeletonLight>
      </div>
    </div>
  );
}

function SecondaryUserApplicationCard({
  id,
  inactive,
  logo,
  name,
  isAuthorized,
  canRequestAccess,
  isLoading,
  requestAccess,
  adminOnly,
  appNotIncluded,
}: {
  id: string;
  inactive: boolean;
  logo: string | null;
  name: string;
  isAuthorized: boolean;
  canRequestAccess: boolean;
  appNotIncluded: boolean;
  adminOnly: boolean;
  isLoading: boolean;
  requestAccess: (id: string) => void;
}) {
  const { handleError, src } = useImgSrcWithFallback(logo, '/images/nos/default-app-image.svg');
  const { t } = useI18n();
  return (
    <div className="flex items-center gap-3 rounded bg-nos-neutral-0 p-4 shadow-1">
      <img
        className="size-8 rounded-lg border-[0.5px] border-nos-neutral-200 object-cover data-[inactive=true]:opacity-50"
        data-inactive={inactive}
        alt={t('Apps.ApplicationIconAlt', { name: name })}
        src={src}
        onError={handleError}
      />
      <span className="text-nos-neutral-900 font-text-p1">{name}</span>
      <div className="ml-auto">
        <If condition={isAuthorized}>
          <span className="font-medium text-nos-green font-text-p2">{t('Apps.AppAuthorized')}</span>
        </If>
        <If condition={canRequestAccess}>
          <Button
            skin="light"
            variant="tertiary"
            disabled={isLoading}
            onClick={() => requestAccess(id)}
            data-tid="button-request-access"
          >
            <If condition={isLoading} otherwise={t('Apps.RequestAccess')}>
              <Loader width={24} height={24} />
            </If>
          </Button>
        </If>
        <If condition={appNotIncluded}>
          <span className="font-light text-nos-neutral-900 font-text-p2">
            {t('Apps.AppNotAvailable')}
          </span>
        </If>
        <If condition={adminOnly}>
          <span className="font-light text-nos-neutral-900 font-text-p2">
            {t('Apps.AppAdminOnly')}
          </span>
        </If>
      </div>
    </div>
  );
}

function PrimaryUserAsideSection({
  removeServices,
  isLoading,
  hasMultipleServices,
}: {
  removeServices: () => void;
  isLoading: boolean;
  hasMultipleServices: boolean;
}) {
  const { t } = useI18n();
  return (
    <div className="grid gap-10 desktop-md:sticky desktop-md:top-28">
      <div className="grid gap-2">
        <If condition={hasMultipleServices}>
          <ActionCard
            type="button"
            skin="light"
            preTitle={t('Services.PrimaryUser.ManageMainAddress.PreTitle')}
            title={t('Services.PrimaryUser.ManageMainAddress.Title')}
            subTitle={<></>}
            variant="quick-link"
            className="w-full bg-nos-neutral-0 shadow-1"
            asChild
            data-tid="card-manage-main-address"
          >
            <Link to="/services/default-household" />
          </ActionCard>
        </If>
        <ActionCard
          type="button"
          skin="light"
          preTitle={t('Services.PrimaryUser.SecondaryUsers.PreTitle')}
          title={t('Services.PrimaryUser.SecondaryUsers.Title')}
          subTitle={<></>}
          variant="quick-link"
          className="w-full bg-nos-neutral-0 shadow-1"
          asChild
          data-tid="card-secondary-users"
        >
          <Link to="/services/users" />
        </ActionCard>
        <ActionCard
          type="button"
          skin="light"
          preTitle={t('Services.PrimaryUser.Apps.PreTitle')}
          title={t('Services.PrimaryUser.Apps.Title')}
          subTitle={<></>}
          variant="quick-link"
          className="w-full bg-nos-neutral-0 shadow-1"
          asChild
          data-tid="card-apps"
        >
          <Link to="/services/apps" />
        </ActionCard>
      </div>
      <div className="grid gap-6">
        <ActionCard
          type="button"
          skin="light"
          data-testid="switch-services-card"
          data-tid="card-switch-services"
          preTitle={t('Services.PrimaryUser.SwitchServicesPreTitle')}
          title={t('Services.SecondaryUser.RemoveServicesTitle')}
          subTitle={<></>}
          variant="menu-secondary"
          className="w-full"
          asChild
        >
          <Link to="/services/switch-services" />
        </ActionCard>
        <RemoveServicesCard isLoading={isLoading} removeServices={removeServices} />
      </div>
    </div>
  );
}

function RemoveServicesCard({
  removeServices,
  isLoading,
  className,
}: {
  removeServices: () => void;
  isLoading: boolean;
  className?: string;
}) {
  const { t } = useI18n();
  const buttonsFn = (close: () => void) => (
    <div className="mt-10 grid gap-4 tablet:grid-cols-2">
      <Button
        skin="light"
        destructive
        type="button"
        className="max-tablet:row-start-2"
        onClick={removeServices}
        disabled={isLoading}
        data-tid="button-remove-services"
      >
        <If
          condition={isLoading}
          otherwise={t('Services.SecondaryUser.RemoveServicesAlert.Remove')}
        >
          <Loader width={24} height={24} />
        </If>
      </Button>
      <Button
        skin="light"
        variant="secondary"
        type="button"
        onClick={close}
        disabled={isLoading}
        data-tid="button-cancel-remove-services"
      >
        {t('Common.Cancel')}
      </Button>
    </div>
  );
  return (
    <Alert
      skin="light"
      className="z-[60]"
      overlayClassName="z-[60]"
      description={t('Services.SecondaryUser.RemoveServicesAlert.Description')}
      title={t('Services.SecondaryUser.RemoveServicesAlert.Title')}
      icon={<Warning width={48} height={48} className="text-nos-red" />}
      buttons={buttonsFn}
      data-tid="alert-remove-services"
    >
      <ActionCard
        type="button"
        skin="light"
        data-testid="remove-services-card"
        data-tid="card-remove-services"
        preTitle={t('Services.SecondaryUser.RemoveServicesPreTitle')}
        title={t('Services.SecondaryUser.RemoveServicesTitle')}
        subTitle={<></>}
        variant="menu-secondary"
        className={cn('w-full', className)}
      />
    </Alert>
  );
}

function PrimaryUserMainSection({
  services,
  defaultAccountId,
}: {
  services: AccountServices;
  defaultAccountId: string | undefined;
}) {
  const { t } = useI18n();
  const serviceAccounts = groupServiceAccounts(services.Holder.ServiceAccounts ?? []);
  const customerId = serviceAccounts[0]?.CustomerId;

  function mainAddressButtons(close: () => void) {
    return (
      <div className="mt-10 grid gap-4 tablet:grid-cols-2">
        <Button skin="light" type="button" onClick={close} data-tid="button-close">
          {t('Common.Close')}
        </Button>
        <Button
          skin="light"
          variant="secondary"
          type="button"
          onClick={close}
          asChild
          data-tid="button-manage-main-address"
        >
          <Link to="/services/default-household">
            {t('Services.PrimaryUser.MainAddressAlert.ManageMainAddressButton')}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-nos-neutral-100 bg-nos-neutral-0 p-4 shadow-1">
      <If condition={!!customerId}>
        <div className="flex flex-col gap-4">
          <span className="text-nos-neutral-500 font-text-p3" data-tid="text-customer-number">
            {t('Services.PrimaryUser.CustomerNumber')}: {customerId}
          </span>
          <div className="mb-4 h-px w-full bg-nos-neutral-200" />
        </div>
      </If>
      <div className="grid">
        {serviceAccounts.map((sa, index) => (
          <div
            key={sa.Id}
            className={cn('grid gap-4', index > 0 && 'mt-4 border-t border-nos-neutral-100 pt-5')}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="grid gap-1">
                <span
                  className="font-medium text-nos-neutral-900 font-title-h4"
                  data-tid="text-package-name"
                >
                  {sa.Package || t('Services.MainAddress.PackageFallback')}
                </span>
                <If condition={sa.Id === defaultAccountId && serviceAccounts.length > 1}>
                  <Alert
                    icon={<Info width={48} height={48} className="text-nos-blue" />}
                    className="z-[60]"
                    overlayClassName="z-[60]"
                    skin="light"
                    title={t('Services.PrimaryUser.MainAddressAlert.Title')}
                    description={t('Services.PrimaryUser.MainAddressAlert.Subtitle')}
                    buttons={mainAddressButtons}
                    data-tid="alert-main-address-info"
                  >
                    <button
                      type="button"
                      className="inline-flex w-fit items-center gap-1 font-medium text-nos-blue font-text-p2"
                      data-tid="button-main-address-info"
                    >
                      {t('Services.PrimaryUser.MainAddressAlert.MainAddressLabel')}{' '}
                      <Info width={18} height={18} />
                    </button>
                  </Alert>
                </If>
              </div>
            </div>
            <div className="grid gap-1">
              <span className="text-nos-neutral-500 font-text-p2">
                {t('Services.AddServices.Success.ServiceId')}
              </span>
              <span className="text-nos-neutral-900 font-text-p1">{sa.Id}</span>
            </div>
            <div className="grid gap-1">
              <span className="text-nos-neutral-500 font-text-p2">
                {t('Services.AddServices.Success.Address')}
              </span>
              <span className="text-nos-neutral-900 font-text-p1">
                {sa.Address || t('Services.MainAddress.AddressFallback')}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
